import React, { useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { CrmButton, CrmPopover, CrmTooltip } from '../ui';
import CrmListToolbar from './CrmListToolbar';
import './crm-listing.scss';

export function CrmListHeader({ title, subtitle, onCreate, createLabel, onImport, onSettings, headerActions }) {
  const actionsRef = useRef(null);
  const [actionsOpen, setActionsOpen] = useState(false);

  return (
    <header className="crm-list-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      <div className="crm-list-header__actions">
        {headerActions}
        {onSettings ? (
          <CrmTooltip content={`${title} settings`} placement="bottom">
            <CrmButton
              iconOnly
              size="small"
              aria-label={`${title} settings`}
              onClick={onSettings}
            >
              <SettingsOutlinedIcon />
            </CrmButton>
          </CrmTooltip>
        ) : null}
        {onImport ? (
          <>
            <CrmTooltip content="More actions" placement="bottom">
              <CrmButton
                ref={actionsRef}
                iconOnly
                size="small"
                aria-label={`More ${title.toLowerCase()} actions`}
                onClick={() => setActionsOpen((previous) => !previous)}
              >
                <MoreVertIcon />
              </CrmButton>
            </CrmTooltip>
            <CrmPopover
              open={actionsOpen}
              onClose={() => setActionsOpen(false)}
              anchorRef={actionsRef}
              align="end"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onImport();
                  setActionsOpen(false);
                }}
              >
                <FileUploadOutlinedIcon /> Import {title.toLowerCase()}
              </button>
            </CrmPopover>
          </>
        ) : null}
        {onCreate ? (
          <CrmButton variant="primary" size="small" startIcon={<AddIcon />} onClick={onCreate}>
            {createLabel}
          </CrmButton>
        ) : null}
      </div>
    </header>
  );
}

export function CrmSavedViews({ views, activeView, onChange, onAddView, onDeleteView }) {
  return (
    <nav className="crm-saved-views" aria-label="Saved views">
      {views.map((view) => (
        <span key={view.id} className={`crm-saved-view-item ${activeView === view.id ? 'is-active' : ''}`}>
          <button
            type="button"
            className={activeView === view.id ? 'is-active' : ''}
            onClick={() => onChange(view.id)}
            aria-current={activeView === view.id ? 'page' : undefined}
          >
            {view.label}
          </button>
          {String(view.id) !== 'all' && onDeleteView ? (
            <button type="button" className="crm-saved-view-item__delete" onClick={() => onDeleteView(view.id)} aria-label={`Delete ${view.label} view`}>
              <CloseIcon />
            </button>
          ) : null}
        </span>
      ))}
      {onAddView ? (
        <CrmTooltip content="Save current table as a new view" placement="bottom">
          <button
            type="button"
            className="crm-saved-views__add"
            onClick={onAddView}
            aria-label="Save a new table view"
          >
            <AddIcon />
          </button>
        </CrmTooltip>
      ) : null}
    </nav>
  );
}

export function CrmBulkActions({ selectedCount, onExport, onDelete, extraActions }) {
  if (!selectedCount) return null;

  return (
    <div className="crm-list-bulk-actions" role="region" aria-label="Bulk record actions">
      <strong>{selectedCount} selected</strong>
      {onExport ? (
        <CrmButton size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={onExport}>Export</CrmButton>
      ) : null}
      {onDelete ? (
        <CrmButton size="small" variant="danger" startIcon={<DeleteOutlineIcon />} onClick={onDelete}>Delete</CrmButton>
      ) : null}
      {extraActions}
    </div>
  );
}

export function CrmListFooter({
  totalItems,
  itemLabel,
  itemPluralLabel,
  currentPage,
  totalPages,
  pageSize,
  onPageSizeChange,
  onPageChange,
  onRefresh,
  onExport,
}) {
  return (
    <footer className="crm-list-footer">
      <div className="crm-list-footer__count">
        <strong>{totalItems}</strong> {totalItems === 1 ? itemLabel : (itemPluralLabel || `${itemLabel}s`)}
      </div>
      <div className="crm-list-footer__utilities">
        <label className="crm-list-footer__page-size">
          <span>Rows</span>
          <select value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        {onRefresh ? (
          <CrmTooltip content="Refresh records">
            <CrmButton iconOnly size="small" onClick={onRefresh} aria-label="Refresh records">
              <RefreshIcon />
            </CrmButton>
          </CrmTooltip>
        ) : null}
        {onExport ? (
          <CrmButton size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={onExport}>Export</CrmButton>
        ) : null}
        <span className="crm-list-footer__page">Page {currentPage} of {totalPages}</span>
        <CrmButton
          iconOnly
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          aria-label="Previous page"
        >
          <NavigateBeforeIcon />
        </CrmButton>
        <CrmButton
          iconOnly
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
        >
          <NavigateNextIcon />
        </CrmButton>
      </div>
    </footer>
  );
}

function CrmListPage({
  title,
  subtitle,
  views,
  activeView,
  onViewChange,
  onCreate,
  createLabel,
  onImport,
  onSettings,
  onAddView,
  onDeleteView,
  headerActions,
  summaryItems,
  chromeCollapsed = false,
  toolbarProps,
  selectedCount,
  onExportSelected,
  onDeleteSelected,
  bulkExtraActions,
  children,
  footerProps,
  hideSavedViews = false,
  hideToolbar = false,
  hideFooter = false,
}) {
  return (
    <section className="crm-list-page">
      {!chromeCollapsed ? (
        <>
          <CrmListHeader
            title={title}
            subtitle={subtitle}
            onCreate={onCreate}
            createLabel={createLabel}
            onImport={onImport}
            onSettings={onSettings}
            headerActions={headerActions}
          />
          {!hideSavedViews ? (
            <CrmSavedViews
              views={views}
              activeView={activeView}
              onChange={onViewChange}
              onAddView={onAddView}
              onDeleteView={onDeleteView}
            />
          ) : null}
          {summaryItems?.length ? (
            <div className="crm-list-summary">
              {summaryItems.map((item) => (
                <div key={item.label} className="crm-list-summary__item">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      {!hideToolbar ? <CrmListToolbar {...toolbarProps} /> : null}
      <CrmBulkActions
        selectedCount={selectedCount}
        onExport={onExportSelected}
        onDelete={onDeleteSelected}
        extraActions={bulkExtraActions}
      />
      <div className="crm-list-page__table">{children}</div>
      {!hideFooter ? <CrmListFooter {...footerProps} /> : null}
    </section>
  );
}

export default CrmListPage;
